import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import moment from 'moment';
import api from '../../services/api';
import { getMediaUrl } from '../../services/api';
import './NewsSection.css';

const NewsSection = ({ section, layout = 'default' }) => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNews();
  }, []);

  const loadNews = async () => {
    try {
      const response = await api.get('/news/', { params: { page_size: 6, featured: true } });
      const newsData = response.data.results || response.data;
      setNews(Array.isArray(newsData) ? newsData : []);
    } catch (error) {
      console.error('Error loading news:', error);
      setNews([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className="news-section homepage-section">
        <Container>
          <h2 className="section-title">{section.title}</h2>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </section>
    );
  }

  if (news.length === 0) return null;

  const renderGridLayout = () => (
    <Row className={news.length < 3 ? 'justify-content-center' : ''}>
      {news.slice(0, 3).map(item => (
        <Col key={item.id} lg={4} md={6} sm={12} className="d-flex">
          <Card className="news-card">
            {item.featured_image && (
              <Card.Img 
                variant="top" 
                src={getMediaUrl(item.featured_image)} 
                alt={item.title}
              />
            )}
            <Card.Body>
              <Card.Text className="text-muted small">
                <i className="far fa-calendar-alt me-2"></i>
                {moment(item.published_date).format('MMMM DD, YYYY')}
              </Card.Text>
              <Card.Title>
                <Link to={`/news/${item.slug}`}>{item.title}</Link>
              </Card.Title>
              <Card.Text>
                {item.summary || item.content.replace(/<[^>]*>/g, '').substring(0, 140)}...
              </Card.Text>
              <Link to={`/news/${item.slug}`} className="btn btn-outline-primary btn-sm">
                Read More <i className="fas fa-arrow-right ms-1"></i>
              </Link>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );

  const renderListLayout = () => (
    <div className="news-list">
      {news.map(item => (
        <div key={item.id} className="news-list-item border-bottom py-3">
          <Row>
            <Col md={3} sm={4} xs={12} className="mb-2 mb-md-0">
              {item.featured_image && (
                <img 
                  src={getMediaUrl(item.featured_image)} 
                  alt={item.title}
                  className="img-fluid rounded"
                />
              )}
            </Col>
            <Col md={9} sm={8} xs={12}>
              <h5>
                <Link to={`/news/${item.slug}`}>{item.title}</Link>
              </h5>
              <p className="text-muted small mb-2">
                <i className="far fa-calendar-alt me-2"></i>
                {moment(item.published_date).format('MMMM DD, YYYY')}
              </p>
              <p className="mb-3">
                {item.summary || item.content.replace(/<[^>]*>/g, '').substring(0, 200)}...
              </p>
              <Link to={`/news/${item.slug}`} className="btn btn-sm btn-outline-primary">
                Read More <i className="fas fa-arrow-right ms-1"></i>
              </Link>
            </Col>
          </Row>
        </div>
      ))}
    </div>
  );

  const renderCarouselLayout = () => (
    <div className="news-carousel">
      <Row className="g-3">
        {news.slice(0, 3).map((item, idx) => (
          <Col key={item.id} lg={4} md={6} sm={12}>
            <Card className="news-card news-card-carousel">
              {item.featured_image && (
                <Card.Img 
                  variant="top" 
                  src={getMediaUrl(item.featured_image)} 
                  alt={item.title}
                  className="carousel-img"
                />
              )}
              <div className="news-overlay">
                <Card.Body className="p-4">
                  <Card.Text className="text-white small mb-3">
                    <i className="far fa-calendar-alt me-2"></i>
                    {moment(item.published_date).format('MMM DD, YYYY')}
                  </Card.Text>
                  <Card.Title className="text-white">
                    <Link to={`/news/${item.slug}`} className="text-white">{item.title}</Link>
                  </Card.Title>
                  <Link to={`/news/${item.slug}`} className="btn btn-light btn-sm mt-3">
                    Read More <i className="fas fa-arrow-right ms-1"></i>
                  </Link>
                </Card.Body>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );

  const renderContent = () => {
    switch(layout) {
      case 'list':
        return renderListLayout();
      case 'carousel':
        return renderCarouselLayout();
      case 'grid':
      case 'default':
      default:
        return renderGridLayout();
    }
  };

  return (
    <section className={`news-section homepage-section news-layout-${layout}`}>
      <Container>
        <h2 className="section-title">{section.title}</h2>
        {renderContent()}
        <div className="text-center mt-4">
          <Link to="/news" className="btn btn-primary">
            View All News <i className="fas fa-arrow-right ms-2"></i>
          </Link>
        </div>
      </Container>
    </section>
  );
};

export default NewsSection;
